import { BaseEditor, Editor, Element, Node, NodeEntry, Path, Range, Transforms } from 'slate'
import { Registry } from '@react-registry'
import React from 'react'
import { useSlate } from 'slate-react'

import { createCell } from './createCell'
import { elementCategories } from '../../elementCategories'
import { TableAddColumnBelowIcon } from '../../../utils/Icon'
import RTE_SHORTCUT from '../../../../../../../utils/common-rte-utils/shortcuts.constant'
import { cellType, isCellSpanning } from './mergeCells'
import { cloneDeep, uniqBy } from 'lodash'
import { getSpanningCell } from './helper'

export const incrementColSpan = (editor: BaseEditor, cellEntry: NodeEntry<Node>) => {
  const [cell, path] = cellEntry
  const newAttrs = cloneDeep(cell['attrs'])
  newAttrs.colSpan++
  newAttrs['redactor-attributes'].colspan = newAttrs.colSpan

  Transforms.setNodes(editor, {
    attrs: newAttrs
  } as Partial<Node>, {
    at: path
  })
}

export const insertColAfter = (event, { editor }) => {
  const cellEntry = Editor.above(editor, {
    match: (n) => {
      return cellType.includes(n["type"])
    },
    mode: 'lowest'
  })

  const tableEntry = Editor.above(editor, {
    match: (n) => {
      return n["type"] === 'table'
    },
    mode: 'lowest'
  })

  if (!tableEntry || !cellEntry) return

  const colIndex = cellEntry[1].at(-1)

  const cellsInCurrentCol =
    Editor.nodes(editor, {
      at: tableEntry[1],
      match: (n, p) => {
        return cellType.includes(n["type"]) && p.at(-1) === colIndex
      },
      mode: 'lowest'
    })

  const cellsToIncrementColSpanTo: NodeEntry<Node>[] = []

  for (const [cell, path] of cellsInCurrentCol) {
    let isVoid = false
    let { colSpan } = cell?.['attrs'] ?? {}

    if (colSpan && colSpan > 1) {
      isVoid = true
      cellsToIncrementColSpanTo.push([cell, path])
    }

    if (Editor.isVoid(editor, cell)) {

      const spanningCellEntry = getSpanningCell(editor, path)

      if (spanningCellEntry) {
        const [spanningCell, spanningCellPath] = spanningCellEntry
        const { hasColSpan } = isCellSpanning(spanningCell)
        if (
          (hasColSpan && spanningCellPath.at(-1) === path.at(-1)) ||
          spanningCellPath.at(-1) + spanningCell?.['attrs']?.colSpan - 1 > path.at(-1)
        ) {
          cellsToIncrementColSpanTo.push(spanningCellEntry)
          isVoid = true
        }
      }
    }

    let cellToInsert = createCell(cell['type'], isVoid)
    Transforms.insertNodes(editor, cellToInsert, { at: Path.next(path) })

  }

  for (const ne of uniqBy(cellsToIncrementColSpanTo, (ne) => ne[1].join(','))) {
    incrementColSpan(editor, ne)
  }

  let colWidths = tableEntry[0]["attrs"].colWidths.slice()
  colWidths.splice(colIndex, 0, 250)
  Transforms.setNodes(
    editor,
    {
      attrs: {
        ...tableEntry[0]["attrs"],
        colWidths,
        cols: tableEntry[0]["attrs"].cols + 1
      }
    } as Partial<Node>,
    { at: tableEntry[1] }
  )

}
const InsertColAfterButton = (props) => {
  const editor = useSlate()
  return (
    <React.Fragment>
      {props.children({
        handleMouseDown: insertColAfter,
        isDisable: { func: insertColAfterButtonDisabler, params: { editor } },
        additionalParams: { editor }
      })}
    </React.Fragment>
  )
}

export const shortcutInsertColAfter = (event, editor, format, registryObj) => {
  insertColAfter(event, { editor })
}

const insertColAfterButtonDisabler = ({ editor }) => {

  if (editor.selection && Range.isExpanded(editor.selection)) return true


  const [table] = Editor.nodes(editor, {
    match: (n) => {
      return n['type'] === 'table'
    },
    mode: 'lowest'
  })

  return !table
}
Registry.register(
  {
    IngressComponent: InsertColAfterButton,
    iconName: <TableAddColumnBelowIcon />,
    toolbar: {
      inSlashToolbar: false
    },
    canCustomize: false,
    shortcut: { key: RTE_SHORTCUT.ADD_COLUMN_RIGHT, callback: shortcutInsertColAfter },
    category: elementCategories.TABLE_ACTION,
    title: 'Insert column right',
    isContentStackElement: true
  },
  {
    id: 'table-insert-col-after',
    registry: 'v2.element'
  }
)
